import { Injectable } from '@angular/core';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchParams } from '../../../../shared/search/models/search-params.model';
import { SearchStatus } from '../../../../shared/search/models/search-status.model';
import { PagedData } from '../../../../shared/search/models/paged-data.model';
import { TableRow } from '../../../../shared/search/models/table-row.model';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../../shared/shared-service-registry';
import { ReportsConfigurationDataService } from './reports-configuration-data.service';
import { SearchEvent } from '../../../../shared/search/models/search-event';
import { INSERT_ROW_EVENT, NEW_ROW_EVENT } from '../../../../shared/search/search-constants';

@Injectable()
export class ReportConfigurationSearchService extends SearchService {

    private dataService: ReportsConfigurationDataService;


    constructor(private coreServiceRegistry: CoreServiceRegistry,
        private sharedServiceRegistry: SharedServiceRegistry,
        public searchParams: SearchParams,
        public searchStatus: SearchStatus) {
        super(coreServiceRegistry.http$, searchParams, searchStatus,
            coreServiceRegistry.resourceService,
            sharedServiceRegistry.userPreferencesService, sharedServiceRegistry.alertService,
            sharedServiceRegistry.dateConfigService);
        this.pagedData = new PagedData<TableRow>();
    }

    public setDataService(dataService: ReportsConfigurationDataService) {
        this.dataService = dataService;
    }

    /**
    * Adds a new row locally (client side only) to the current data table. The new row will be put to the first position
    */
    public addNewRowToModel() {
        // overriding method for -  if primary id (reportId) field is empty, then adding new row should not allowed
        if (this.dataService.getPrimaryDataIdValue()) {
            super.addNewRowToModel();
        } else {
            this.alertService.clearAll().error(this.resourceService.get('err_report_id_empty'));
            return;
        }
    }

    protected createNewRow(insertPosition?: number): any {
        // if insertPostion is not given it will work as add new row , if insert postion is given new row will be inserted at that position
        if (!this.metadata || !this.metadata['columnList']) {
            return 'meta data not loaded';
        }
        const cellData = {};
        const cellDataFormatter = (column: any) => {
            const propName = column.propertyName;
            const propType = column.type;
            const dateDefaultValue = column.defaultVal;
            if (propType === 'TEXTFIELD') {
                let defaultValue: any;
                if (column.dataType === 'Integer') {
                    if (column.defaultVal && column.defaultVal !== '') {
                        defaultValue = column.defaultVal;
                    } else {
                        defaultValue = 0;
                    }
                } else if (column.dataType === 'String') {
                    if (column.defaultVal && column.defaultVal !== '') {
                        defaultValue = column.defaultVal;
                    } else {
                        defaultValue = '';
                    }
                } else if (column.dataType === 'Float') {
                    if (column.defaultVal && column.defaultVal !== '') {
                        defaultValue = column.defaultVal;
                    } else {
                        defaultValue = 0.000000;
                    }
                } else if (column.dataType === 'Currency') {
                    if (column.defaultVal && column.defaultVal !== '') {
                        defaultValue = column.defaultVal;
                    } else {
                        defaultValue = '0.00';
                    }
                } else {
                    defaultValue = null;
                }
                cellData[propName] = { cVal: defaultValue, cName: propName };
            } else if (propType === 'DATE' || propType === 'DATETIME') {
                // To set null value if no default value starts
                if (!cellData[dateDefaultValue]) {
                    cellData[propName] = { cName: propName, cVal: null };
                } else {
                    if (propType === 'DATE') {
                        cellData[propName] = { cName: propName, cVal: this.dateConfigService.getFormattedDisplayDate(Date.now()) };
                    } else if (propType === 'DATETIME') {
                        cellData[propName] = { cName: propName, cVal: this.dateConfigService.getFormattedDisplayDateTime(Date.now()) };
                    }
                }
                // To set null value if no default value ends

            } else if (propType === 'DROPDOWN' || propType === 'EDITABLEDROPDOWN') {
                let defaultOption = '';
                if (cellData['storerKey'] && cellData['storerKey'].cVal) {
                    const storer = cellData['storerKey'].cVal;
                    if (!column.originalCoptions) {
                        column.originalCoptions = column.values;
                    }
                    column.values = this.getValuesBasedOnStorer(column.originalCoptions, storer);
                }
                if (column.values[0] && column.values[0].key) {
                    defaultOption = column.defaultVal; // To set the default value if the user does not select any options
                }
                cellData[propName] = { cName: propName, cVal: defaultOption };
            } else if (propType === 'CHECKBOX' || propType === 'CHECKBOX_CHR') {
                // Adding CHECKBOX_CHR type denoting the saved value will be 'Y' or 'N'
                // CHECKBOX type denoting the saved value will be '1' or '0'
                cellData[propName] = (propName == 'autoPrint') ? { cVal: 'N' } : {cVal: false};
            } else if (propType === 'DROPDOWN_MC') {
                let defaultValue: any;
                defaultValue = '';
                cellData[propName] = { cName: propName, cVal: defaultValue };
            } else if (propType === 'DROPDOWN_MS') {
                let defaultValue: any;
                if (column.defaultVal && column.defaultVal !== '') {
                    defaultValue = column.defaultVal;
                } else {
                    defaultValue = '';
                }
                cellData[propName] = { cName: propName, cVal: defaultValue };
            } else if (propType === 'TIME24') {
                let defaultValue: any;
                defaultValue = '00:00';
                cellData[propName] = { cName: propName, cVal: defaultValue };
            }
        };
        const storerKeyColumn = this.metadata['columnList'].find((column: any) => column.propertyName === 'storerKey');
        if (storerKeyColumn) {
            cellDataFormatter(storerKeyColumn);
        }
        this.metadata['columnList'].forEach(cellDataFormatter);
        cellData['isNewItem'] = { cVal: true };
        const rowData = new TableRow();
        rowData.isSelected = false;
        rowData.isEdited = true;
        rowData.isNewRow = true;
        rowData.cells = cellData;
        this.pagedData.pageDefinition.recordCount++;
        rowData['rIdx'] = this.pagedData.pageRows.length + 1;
        if (!insertPosition) { // addmode
            // changing the logic as part of JIRA-649 to display the newly added row in the top above all rows in the grid
            if (!this.pagedData.pageRows) {
                this.pagedData.pageRows = [];
                this.pagedData.pageRows.push(rowData);
            } else {
                this.pagedData.pageRows.splice(0, 0, rowData);
            }
        } else {
            // mode === insert
            this.pagedData.pageRows.splice(insertPosition, 0, rowData);

        }
        // this.pagedData.pageRows.push(rowData);
        if (!this.searchStatus.hasResults) {
            this.searchStatus.hasResults = true;
        }
        return rowData;
    }
    public getValuesBasedOnStorer(originalCoptions: any, storer: any) {
        const storerKey = storer;
        let filteredCoptions;
        if (storerKey && storerKey.cVal) {
            filteredCoptions = originalCoptions.filter(element => element.storerkey &&
                element.storerkey.toUpperCase() === storerKey.cVal.trim().toUpperCase());
            if (filteredCoptions.length > 0) {
                filteredCoptions.unshift({ key: '', value: '- Select -', storerkey: null, userDefineValues: null });
            }
        }
        if (!filteredCoptions || filteredCoptions.length === 0) {
            filteredCoptions = originalCoptions.filter(element => !element.storerkey);
        }
        if (filteredCoptions.length === 1 && filteredCoptions[0].key === '' &&
            filteredCoptions[0].value.toLowerCase() === '- select -') {
            filteredCoptions = [];
        }
        return filteredCoptions;
    }
}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/features/reports/reports-configuration/services/reports-configuration-search.service.ts